/*
 * Service responsible for managing the dynamic form creation,
 * field synchronization, and state maintenance.
 */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { environment } from '@siat-fe/env';
import { catchError, EMPTY, map, Observable, tap } from 'rxjs';
import { SCALE_FIELD_TYPE } from '../constants/field-types';
import {
  DynamicFormDTO,
  FieldOption,
  FieldTypeENUM,
  IDynamicForm,
  IFields,
  ISection,
} from '../models';
import { ScaleDefinition } from '../models/dynamic-scale.model';
import { FormMode } from '../models/form-mode.enum';

/**
 * A typology field endpoint, either a raw endpoint name or the resolved item selected from the picker.
 */
export type EndpointRef = string | { name: string };

@Injectable()
export class FormBuilderService {
  /*
   * Injected HTTP Client for network requests.
   */
  private readonly httpClient = inject(HttpClient);

  /*
   * Injected Angular FormBuilder for reactive forms creation.
   */
  private readonly fb = inject(FormBuilder);

  /*
   * Stores the current dynamic form Data Transfer Object.
   */
  dynamicFormDTO: DynamicFormDTO;

  /*
   * Reactive signal holding the entire form group state.
   */
  readonly formGroup: WritableSignal<FormGroup | null> = signal(null);

  /**
   * Reactive signal holding the list of available endpoints.
   */
  private readonly _availableEndpoints = signal<{ id: string; name: string }[]>(
    [],
  );

  /**
   * Readonly signal for the list of available endpoints.
   */
  readonly availableEndpoints = this._availableEndpoints.asReadonly();

  /**
   * Flag indicating if tipology loading is in progress.
   */
  private isTipologyLoading = false;

  /*
   * Predefined fixed labels mapped to specific field types.
   */
  private readonly FIXED_LABELS: Partial<Record<FieldTypeENUM, string>> = {
    [FieldTypeENUM.PRIORITY]: 'PRIORITÀ',
    [FieldTypeENUM.BARTHEL_SCALE]: 'Indice di Barthel',
    [FieldTypeENUM.BRADEN_SCALE]: 'Scala di Braden',
    [FieldTypeENUM.MINIMENTAL_SCALE]: 'Mini-Mental State Examination (MMSE)',
    [FieldTypeENUM.NRS_SCALE]: 'Scala NRS',
    [FieldTypeENUM.VAS_SCALE]: 'Scala VAS',
    [FieldTypeENUM.NAZIONE]: 'Nazione',
    [FieldTypeENUM.EMAIL]: 'Email',
    [FieldTypeENUM.TELEFONO]: 'Telefono',
    [FieldTypeENUM.CODICE_FISCALE]: 'Codice fiscale',
    [FieldTypeENUM.REGIONI_PROVINCE_COMUNI]: 'Regione / Provincia / Comune',
  };

  /*
   * Categorizes field types into logical groupings for easier processing.
   */
  private readonly TYPE_GROUPS = {
    OPTIONS: [
      FieldTypeENUM.SELECT,
      FieldTypeENUM.RADIOBUTTON,
      FieldTypeENUM.CHECKBOX,
    ],
    RANGE: [FieldTypeENUM.NUMBER, FieldTypeENUM.TEXT, FieldTypeENUM.TEXTAREA],
    PLACEHOLDER: [FieldTypeENUM.TEXT, FieldTypeENUM.TEXTAREA],
    FIXED_LABEL: [
      FieldTypeENUM.PRIORITY,
      FieldTypeENUM.BARTHEL_SCALE,
      FieldTypeENUM.BRADEN_SCALE,
      FieldTypeENUM.MINIMENTAL_SCALE,
      FieldTypeENUM.NRS_SCALE,
      FieldTypeENUM.VAS_SCALE,
      FieldTypeENUM.NAZIONE,
      FieldTypeENUM.EMAIL,
      FieldTypeENUM.TELEFONO,
      FieldTypeENUM.CODICE_FISCALE,
      FieldTypeENUM.REGIONI_PROVINCE_COMUNI,
    ],
  };

  /**
   * Builds a FormGroup based on the provided dynamic form data and mode.
   * @returns {FormGroup} The constructed FormGroup instance.
   */
  buildForm(data: IDynamicForm, mode: FormMode = FormMode.Building): FormGroup {
    const sectionsArray = (data?.sections || []).map((section) =>
      this.buildSection(section, mode),
    );

    return this.fb.group({
      id: [data?.id],
      title: [data?.title || '', Validators.required],
      description: [data?.description],
      sections: this.fb.array(sectionsArray),
    });
  }

  /**
   * Builds a FormGroup for a section, including its fields, based on the provided data and mode.
   * @return {FormGroup} The constructed FormGroup for the section.
   */
  buildSection(
    section: Partial<ISection>,
    mode: FormMode = FormMode.Building,
  ): FormGroup {
    const sectionId = section.id || this.generateId('section', 0);
    const fieldsArray =
      section.fields && section.fields.length > 0
        ? section.fields.map((field) => this.buildField(field, mode))
        : [this.buildField({ type: FieldTypeENUM.TEXT }, mode)];

    return this.fb.group({
      id: [sectionId],
      title: [
        section.title || '',
        [Validators.maxLength(50), Validators.required],
      ],
      description: [section.description],
      fields: this.fb.array(fieldsArray),
    });
  }

  /**
   * Builds a FormGroup for a field based on the provided field data and mode.
   * @returns {FormGroup} The constructed FormGroup for the field.
   */
  buildField(
    field: Partial<IFields>,
    mode: FormMode = FormMode.Building,
  ): FormGroup {
    const isBuilder = mode === FormMode.Building || mode === FormMode.Editing;
    const group = this.createCoreGroup(field, isBuilder);

    this.addPlaceholderControl(group, field, isBuilder);
    this.addRangeControls(group, field);
    this.addOptionsControl(group, field);
    this.addAccordionFieldsControl(group, field, mode);
    this.addScaleConfigControl(group, field);
    this.addTypologyEndpointControl(group, field);

    return group;
  }

  /**
   * Synchronizes the field form group when its type changes,
   * ensuring that only relevant controls are present and that values are cleaned according to the new type.
   */
  syncFieldType(
    fieldForm: FormGroup,
    newType: FieldTypeENUM,
    config?: ScaleDefinition,
    oldTypeOverride?: FieldTypeENUM,
  ): void {
    const currentValues = fieldForm.getRawValue();
    const oldType = oldTypeOverride || (currentValues.type as FieldTypeENUM);

    const label = this.buildLabel(currentValues.label, oldType, newType);
    const cleanDTO = this.cleanFieldDTO(currentValues, newType, label, config);
    const refreshedGroup = this.buildField(cleanDTO, FormMode.Editing);

    this.removeOrAssignControl(fieldForm, refreshedGroup);
  }

  /**
   * Cleans the field data transfer object based on the new type,
   * removing or resetting properties that are no longer relevant.
   * @returns {Partial<IFields>} The cleaned field DTO with properties adjusted for the new type.
   */
  private cleanFieldDTO(
    current: IFields,
    newType: FieldTypeENUM,
    label: string,
    config: ScaleDefinition,
  ): Partial<IFields> {
    const isRangeType = this.TYPE_GROUPS.RANGE.includes(newType);
    const isValidForCond = this.isTypeValidForCondition(newType);

    return {
      id: current.id,
      type: newType,
      label: this.computeTypologyLabel(current, newType, label),
      required: false,
      value: null,
      config: config || null,
      options: this.computeCleanedOptions(current, newType),
      min: isRangeType ? (current.min ?? 0) : undefined,
      max: isRangeType ? current.max : undefined,
      placeholder: this.computeTypologyPlaceholder(current, newType),
      endpoint:
        newType === FieldTypeENUM.TYPOLOGY ? current?.endpoint : undefined,
      fields:
        newType === FieldTypeENUM.ACCORDION
          ? this.computeAccordionFields(current)
          : undefined,
      conditionFieldId: isValidForCond
        ? this.extractId(current?.conditionFieldId)
        : null,
      conditionValue: isValidForCond
        ? this.extractId(current?.conditionValue)
        : null,
      hasCondition: isValidForCond ? current?.hasCondition : false,
    };
  }

  /**
   * @returns {string} The label for the field based on its type and previous label.
   */
  private buildLabel(
    oldLabel: unknown,
    oldType: FieldTypeENUM,
    newType: FieldTypeENUM,
  ): string {
    const isNewFixed = !!this.FIXED_LABELS[newType];
    const wasOldFixed = this.TYPE_GROUPS.FIXED_LABEL.includes(oldType);
    const cleanOldLabel = typeof oldLabel === 'string' ? oldLabel : '';

    if (isNewFixed) return this.FIXED_LABELS[newType]!;
    if (wasOldFixed) return '';

    return cleanOldLabel;
  }

  /**
   * Generates a unique ID for sections, fields,
   * or options based on the provided prefix and current timestamp.
   */
  private createCoreGroup(
    field: Partial<IFields>,
    isBuilder: boolean,
  ): FormGroup {
    const type = field.type || FieldTypeENUM.TEXT;
    const isScale = this.isScaleField(type);
    const labelValue =
      this.FIXED_LABELS[type] ??
      (typeof field.label === 'string' ? field.label : '');

    const group = this.fb.group({
      id: [field.id || this.generateId('field', 0)],
      type: [type],
      label: [labelValue, isBuilder ? Validators.required : []],
      required: [
        {
          value: field.required ?? false,
          disabled: type === FieldTypeENUM.TOGGLE,
        },
      ],
      value: isScale
        ? this.buildScaleFormGroupFromConfig(field.config!)
        : [field.value ?? null],
    });

    this.manageConditionalControls(group, field.hasCondition || false, field);

    return group;
  }

  /**
   * Metodo pubblico per aggiornare dinamicamente la struttura del form condizionale
   */
  manageConditionalControls(
    group: FormGroup,
    hasCondition: boolean,
    data?: Partial<IFields>,
  ): void {
    const conditionProp = [
      'hasCondition',
      'conditionFieldId',
      'conditionValue',
    ];

    if (hasCondition) {
      conditionProp.forEach((prop) => {
        if (!group.contains(prop)) {
          group.addControl(prop, this.fb.control(data?.[prop] || null));
        }
      });
    } else {
      conditionProp.forEach((prop) => {
        if (group.contains(prop)) {
          group.removeControl(prop);
        }
      });
    }
  }

  /**
   * Helper function to build placeholder control if the field type supports it.
   */
  private addPlaceholderControl(
    group: FormGroup,
    field: Partial<IFields>,
    isBuilder: boolean,
  ): void {
    const type = field.type || FieldTypeENUM.TEXT;
    if (this.isScaleField(type) || type === FieldTypeENUM.PRIORITY) return;

    const placeholder = field.placeholder || '';
    const isRequired =
      isBuilder &&
      (type === FieldTypeENUM.TEXT || type === FieldTypeENUM.TEXTAREA);

    group.addControl(
      'placeholder',
      this.fb.control(placeholder, isRequired ? [Validators.required] : []),
    );
  }

  /**
   * Helper function to add min and max controls for range-type fields,
   * ensuring they are only present when relevant.
   */
  private addRangeControls(group: FormGroup, field: Partial<IFields>): void {
    if (this.TYPE_GROUPS.RANGE.includes(field.type!)) {
      group.addControl(
        'min',
        this.fb.control(field.min ?? 0, Validators.required),
      );
      group.addControl('max', this.fb.control(field.max));
    }
  }

  /**
   * Helper function to add options FormArray for fields that support options,
   * initializing it with existing options or a default empty option.
   */
  private addOptionsControl(group: FormGroup, field: Partial<IFields>): void {
    if (this.TYPE_GROUPS.OPTIONS.includes(field.type!)) {
      const options = field.options?.map((o) => this.buildOptionGroup(o)) || [
        this.buildOptionGroup(),
      ];
      group.addControl('options', this.fb.array(options));
    }
  }

  /**
   * Adds nested fields for accordion container fields.
   */
  private addAccordionFieldsControl(
    group: FormGroup,
    field: Partial<IFields>,
    mode: FormMode,
  ): void {
    if (field.type !== FieldTypeENUM.ACCORDION) return;

    const fields = this.computeAccordionFields(field).map((child) =>
      this.buildField(child, mode),
    );
    group.addControl('fields', this.fb.array(fields));
  }

  /**
   * Helper function to build a FormGroup for Scale field configuration
   * based on the provided ScaleDefinition.
   */
  private addScaleConfigControl(
    group: FormGroup,
    field: Partial<IFields>,
  ): void {
    if (this.isScaleField(field.type!) && field.config) {
      group.addControl('config', this.fb.control(field.config));
    }
  }

  /**
   * Helper function to build a FormGroup for endpoint control,
   * ensuring it's only added for typology fields.
   */
  private addTypologyEndpointControl(
    group: FormGroup,
    field: Partial<IFields>,
  ): void {
    if (field.type === FieldTypeENUM.TYPOLOGY) {
      group.addControl(
        'endpoint',
        this.fb.control(field.endpoint || null, Validators.required),
      );
    }
  }

  /**
   * @returns {string} for ID.
   */
  private extractId(value: unknown): string | null {
    if (!value) return null;
    return typeof value === 'object'
      ? ((value as { id?: string }).id ?? null)
      : (value as string);
  }

  /**
   * Determines if a given field type is considered a "trigger" type that can be used for conditional logic.
   */
  private isTriggerValid(type: FieldTypeENUM): boolean {
    return (
      type !== FieldTypeENUM.UPLOADFILE &&
      type !== FieldTypeENUM.SECTION &&
      type !== FieldTypeENUM.ACCORDION &&
      type !== FieldTypeENUM.DATE &&
      !this.isScaleField(type)
    );
  }

  /**
   * @returns {boolean} whether the type supports being a condition trigger,
   * meaning it can be referenced by other fields for conditional visibility or logic.
   */
  private isTypeValidForCondition(type: FieldTypeENUM): boolean {
    return (
      type !== FieldTypeENUM.SECTION &&
      type !== FieldTypeENUM.ACCORDION &&
      type !== FieldTypeENUM.UPLOADFILE &&
      !this.isScaleField(type)
    );
  }

  /**
   * For typology field. Build label.
   */
  private computeTypologyLabel(
    current: IFields,
    type: FieldTypeENUM,
    label: string,
  ): string {
    if (type !== FieldTypeENUM.TYPOLOGY) return label;
    const name = this.extractEndpointName(current.endpoint);
    return name || label;
  }

  /**
   * For typology field. Build placeholder.
   */
  private computeTypologyPlaceholder(
    current: IFields,
    type: FieldTypeENUM,
  ): string {
    if (type === FieldTypeENUM.TYPOLOGY) {
      const name = this.extractEndpointName(current.endpoint);
      return name ? `Ricerca ${name}` : current.placeholder || '';
    }
    return this.TYPE_GROUPS.PLACEHOLDER.includes(type)
      ? current.placeholder || ''
      : '';
  }

  /**
   * Extract endpoint name.
   */
  private extractEndpointName(endpoint: EndpointRef | undefined): string {
    if (!endpoint) return '';
    return typeof endpoint === 'string' ? endpoint : endpoint?.name || '';
  }

  /**
   * Clean options.
   */
  private computeCleanedOptions(
    current: IFields,
    type: FieldTypeENUM,
  ):
    | {
        name?: string;
        id?: string | number;
        disabled?: boolean;
        selected?: boolean;
      }[]
    | undefined {
    if (!this.TYPE_GROUPS.OPTIONS.includes(type)) return undefined;
    return current.options?.length && current.label
      ? current.options
      : [{ id: this.generateId('opt', 0), name: '', selected: false }];
  }

  /**
   * Builds default nested fields for accordion fields.
   */
  private computeAccordionFields(current: Partial<IFields>): IFields[] {
    if (
      current.type === FieldTypeENUM.ACCORDION &&
      current.fields &&
      current.fields.length > 0
    ) {
      return current.fields;
    }

    return [
      {
        id: this.generateId('accordion_field', 0),
        type: FieldTypeENUM.TEXT,
        label: '',
        required: false,
        value: null,
        placeholder: '',
      },
    ];
  }

  /**
   * Remove or assign control.
   */
  private removeOrAssignControl(target: FormGroup, reference: FormGroup): void {
    Object.keys(target.controls).forEach((key) => {
      if (key !== 'id' && !reference.contains(key)) target.removeControl(key);
    });

    Object.keys(reference.controls).forEach((key) => {
      if (key !== 'id') target.setControl(key, reference.get(key)!);
    });
    target.updateValueAndValidity({ emitEvent: true });
  }

  /**
   * @returns {boolean} it tell if the condition still valid.
   */
  isConditionStillValid(childFieldForm: FormGroup): boolean {
    const condFieldId = childFieldForm.get('conditionFieldId')?.value;
    const hasCondition = childFieldForm.get('hasCondition')?.value;

    if (!hasCondition || !condFieldId) return true;

    const sections = this.getSectionFormArray();
    if (!sections) return false;

    let parentGroup: FormGroup | null = null;
    for (const sectionCtrl of sections.controls) {
      const fieldsArray = sectionCtrl.get('fields') as FormArray;
      if (fieldsArray) {
        for (const fieldCtrl of fieldsArray.controls) {
          if (fieldCtrl.get('id')?.value === condFieldId) {
            parentGroup = fieldCtrl as FormGroup;
            break;
          }
        }
      }
      if (parentGroup) break;
    }

    if (!parentGroup) return false;

    const parentType = parentGroup.get('type')?.value as FieldTypeENUM;
    const parentLabel = parentGroup.get('label')?.value;
    const condValue = childFieldForm.get('conditionValue')?.value;

    if (!this.isTriggerValid(parentType)) return false;

    const isFixedLabelType = this.TYPE_GROUPS.FIXED_LABEL.includes(parentType);
    if (!isFixedLabelType && (!parentLabel || parentLabel.trim() === '')) {
      return false;
    }

    const isOptionBased = this.TYPE_GROUPS.OPTIONS.includes(parentType);
    if (isOptionBased && condValue) {
      const options = parentGroup.get('options') as FormArray;
      if (!options || options.length === 0) return false;

      const targetId = typeof condValue === 'object' ? condValue.id : condValue;
      const optionExists = options
        .getRawValue()
        .some((opt) => String(opt.id) === String(targetId));
      if (!optionExists) {
        return false;
      }
    }

    return true;
  }

  /**
   * Build scale formgroup from config
   * @returns { FormGroup }
   */
  private buildScaleFormGroupFromConfig(config: ScaleDefinition): FormGroup {
    const controls: Record<string, unknown[]> = {};

    config?.sections?.forEach((s) => {
      s.fields.forEach((f) => {
        controls[f.key] = [
          f.min || 0,
          [
            Validators.required,
            Validators.min(f.min || 0),
            Validators.max(f.max || 100),
          ],
        ];
      });
    });

    config?.computedFields?.forEach((cf) => {
      controls[cf.key] = [0];
    });

    return this.fb.group(controls);
  }

  /**
   * Clone formgroup.
   * @returns { Formgroup }
   */
  cloneFormGroup(group: FormGroup, generateId?: () => string): FormGroup {
    const clonedControls: Record<string, AbstractControl> = {};
    Object.keys(group.controls).forEach((key) => {
      const control = group.get(key);
      if (control) {
        clonedControls[key] =
          key === 'id' && generateId
            ? this.fb.control(generateId())
            : this.deepCloneControl(control, generateId);
      }
    });
    return this.fb.group(clonedControls);
  }

  /**
   * Clone controls.
   * @returns {AbstractControl}
   */
  private deepCloneControl(
    control: AbstractControl,
    generateId?: () => string,
  ): AbstractControl {
    if (control instanceof FormControl) {
      return this.fb.control(control.value, {
        validators: control.validator,
        asyncValidators: control.asyncValidator,
      });
    }
    if (control instanceof FormGroup)
      return this.cloneFormGroup(control, generateId);
    if (control instanceof FormArray) {
      const clonedArray = this.fb.array([] as AbstractControl[]);
      control.controls.forEach((c) =>
        clonedArray.push(this.deepCloneControl(c, generateId)),
      );
      return clonedArray;
    }
    return this.fb.control(control.value);
  }

  /**
   * Update typology field.
   */
  updateTypologyFieldConfig(group: FormGroup, endpoint: EndpointRef): void {
    if (!endpoint) return;
    const name = typeof endpoint === 'string' ? endpoint : endpoint?.name || '';
    group.patchValue(
      { label: name, placeholder: `Ricerca ${name}` },
      { emitEvent: false },
    );
  }

  /**
   * Add option.
   */
  addOptionToField(form: FormGroup): void {
    (form.get('options') as FormArray).push(this.buildOptionGroup());
  }

  /**
   * Remove option.
   */
  removeOptionFromField(form: FormGroup, index: number): void {
    const options = form.get('options') as FormArray;
    if (options.length > 1) options.removeAt(index);
  }

  /**
   * Adds a nested field inside an accordion field.
   */
  addFieldToAccordion(form: FormGroup, accordionFieldId: string): void {
    const fields = form.get('fields') as FormArray;
    if (!fields) return;

    fields.push(
      this.buildField(
        {
          id: this.generateId(`${accordionFieldId}_field`, fields.length),
          type: FieldTypeENUM.TEXT,
        },
        FormMode.Editing,
      ),
    );
  }

  /**
   * Duplicates a nested accordion field.
   */
  duplicateAccordionField(
    accordionForm: FormGroup,
    fieldFormGroup: FormGroup,
    accordionFieldId: string,
  ): void {
    const fields = accordionForm.get('fields') as FormArray;
    if (!fields) return;

    const index = fields.controls.indexOf(fieldFormGroup);
    if (index === -1) return;

    const duplicatedField = this.cloneFormGroup(fieldFormGroup, () =>
      this.generateId(`${accordionFieldId}_field`, fields.length),
    );

    fields.insert(index + 1, duplicatedField);
  }

  /**
   * Deletes a nested accordion field.
   */
  deleteAccordionField(
    accordionForm: FormGroup,
    fieldFormGroup: FormGroup,
  ): void {
    const fields = accordionForm.get('fields') as FormArray;
    if (!fields) return;

    const index = fields.controls.indexOf(fieldFormGroup);
    if (index !== -1) fields.removeAt(index);
  }

  /**
   * Build options group.
   */
  private buildOptionGroup(option?: FieldOption): FormGroup {
    return this.fb.group({
      id: [option?.id || this.generateId('opt', 0)],
      name: [option?.name || '', Validators.required],
      selected: [option?.selected ?? false],
    });
  }

  /**
   * @returns  {boolean} if the type is scale.
   */
  isScaleField = (type: FieldTypeENUM): boolean =>
    SCALE_FIELD_TYPE.map((t) => t.type).includes(type);

  /**
   * Genrerate id.
   */
  generateId = (prefix: string, index: number): string =>
    `${prefix}_${index}_${Math.random().toString(36).substring(2, 7)}`;

  /**
   * Set formgroup.
   */
  setFormGroup(fg: FormGroup): void {
    this.formGroup.set(fg);
  }

  /**
   * Set dynamic form.
   */
  setDynamicFormDTO(dto: DynamicFormDTO | undefined): void {
    this.dynamicFormDTO = dto;
  }

  /**
   * @returns {FormArray}
   */
  getSectionFormArray(): FormArray | null {
    return this.formGroup()?.get('sections') as FormArray;
  }

  /**
   *  Load tipology,
   */
  loadTipologiche(): void {
    if (this._availableEndpoints().length > 0 || this.isTipologyLoading) return;

    this.isTipologyLoading = true;
    this.getAvailableTipologiche()
      .pipe(
        map((list) => list.map((str) => ({ id: str, name: str }))),
        tap((res) => {
          this._availableEndpoints.set(res);
          this.isTipologyLoading = false;
        }),
        catchError(() => {
          this.isTipologyLoading = false;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  /**
   * Use http client for retrieve the available typology.
   */
  getAvailableTipologiche(): Observable<string[]> {
    return this.httpClient.get<string[]>(
      `${environment.baseUrl}/v1/tipology/findAll`,
    );
  }
}
